import React from "react";

import { footerItems, getItems, headerItems } from "@/utils/sidebarItems";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/uiComponents/components/ui/sidebar";

import { ROUTES } from "@/enums/routes";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";

import type { Profile } from "@/types/authentication";
import { logout } from "@/store/redux/authSlice";
import { ToastSuccessMessages } from "@/enums/errorMessages";

import { logoutUser } from "@/modules/authenticationService";
import { toastMessage } from "@/utils/toastHelper";
import { ErrorHelper } from "@/utils/errorHelper";

export function SideBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isExpanded = state === "expanded";
  const role = useSelector((state: RootState) => state.userDetail.role);
  const user = useSelector(
    (state: RootState) => state.auth.profile,
  ) as Profile | null;

  const accessToken = user?.accessToken;
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );

  const location = useLocation();
  const items = getItems(role);

  const handleLogout = async () => {
    try {
      if (!user?.refreshToken) return;
      await logoutUser(user?.refreshToken);
      dispatch(logout());

      toastMessage("success", ToastSuccessMessages.LOGOUT_SUCCESS);
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const allowedPaths = [
    ROUTES.ADDEXPENSE,
    ROUTES.BRANCHES,
    ROUTES.CATEGORIES,
    ROUTES.DASHBOARD,
    ROUTES.EXPORT_PDF,
    ROUTES.MEMBERS,
    ROUTES.INVITATIONS,
    ROUTES.PROFILE,
    ROUTES.ITEMS,
    `${ROUTES.UPDATE_EXPENSE}/:id`,
  ];

  const isPathAllowed = allowedPaths.some((path) =>
    matchPath({ path, end: true }, location.pathname),
  );

  if (!userDetails || !isPathAllowed) {
    return null;
  }

  if (!accessToken) {
    return null;
  } else {
    return (
      <Sidebar collapsible="icon">
        <SidebarHeader className="flex items-center justify-between px-2  h-[10vh]">
          <SidebarMenu>
            <div className="flex items-center justify-between w-full">
              {headerItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url} className="flex items-center gap-2">
                      <div className="flex gap-2">
                        <img
                          src={item.iconDefault}
                          alt={item.title}
                          className="w-10 h-10 -ml-3 "
                        />
                        <span className="text-4xl font-extrabold text-black font-serif">
                          {item.title}
                        </span>
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              <SidebarTrigger
                className={
                  "flex items-center cursor-pointer justify-center rounded-md transition hover:bg-gray-200 dark:hover:bg-gray-700"
                }
              />
            </div>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map((item) => {
                  const isActive = matchPath(
                    { path: item.url, end: true },
                    location.pathname,
                  );

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        data-active={isActive ? true : undefined} // <-- pass this
                      >
                        <Link to={item.url}>
                          <div className="flex gap-2 items-center">
                            {item.iconDefault && item.iconActive ? (
                              // If image icons exist, use <img>
                              <img
                                src={
                                  isActive ? item.iconActive : item.iconDefault
                                }
                                height={item.height}
                                width={item.width}
                                alt={item.title}
                                // className={`w-5 h-5 `}
                              />
                            ) : item.icon ? (
                              // Otherwise render SVG component with color style based on active
                              <item.icon
                                className={`w-5 h-5  ${isActive ? "text-[#0C0C0C]" : "text-[#3A3A3A]"}`}
                              />
                            ) : null}
                            <span>{item.title}</span>
                          </div>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="mt-auto p-2">
          <SidebarMenu>
            {/* Logout Button */}
            {footerItems.map((item) => (
              <SidebarMenuItem onClick={handleLogout} key={item.title}>
                <SidebarMenuButton asChild>
                  <Link to={item.url} className="flex items-center gap-2">
                    <div className="flex gap-3">
                      {" "}
                      <item.icon className="w-5 h-5 mt-0.5" />
                      {isExpanded && <span>{item.title}</span>}
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    );
  }
}
